import { Avatar, Button, Card, CardBody, CardFooter, CardHeader, Input, Skeleton, Spinner } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpDownIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { type Address, erc20Abi, formatUnits, isAddressEqual } from "viem";
import { useAccount, useReadContract } from "wagmi";
import DividerDark from "../../assets/divider-dark.svg?react";
import { getTokens } from "../../lib/addresses";
import type { BridgeFormData } from "../../mutations/bridge";
import { l2AccountAddressQuery } from "../../queries/aztec";
import { useRegisteredTokens } from "../../queries/tokenReg";
import { useTokenBalance } from "../../queries/useTokenBalances";
import { BridgeTokenSelector } from "./BridgeTokenSelector";
import { BRIDGE_LABELS, type BridgeType, L1_CHAIN_ID } from "./bridgeConfig";

export function BridgeForm({ onReview }: { onReview: (bridgeFormData: BridgeFormData) => void }) {
  const { address: l1AccountAddress } = useAccount();
  const { data: l2AccountAddress } = useQuery(l2AccountAddressQuery);
  const { data: registeredTokens, isLoading: isLoadingTokens } = useRegisteredTokens();

  const [sourceToken, setSourceToken] = useState<Address>(() => {
    const tokens = getTokens();
    return (
      tokens.USDT?.l1Address ??
      tokens.DAI?.l1Address ??
      tokens.WETH?.l1Address ??
      tokens.USDC?.l1Address ??
      "0x0000000000000000000000000000000000000000"
    );
  });
  const [transferAmount, setTransferAmount] = useState("0.00");
  const { bridgeType, toggleBridgeType } = useToggleBridgeType();

  // Set sourceToken to a valid registered token once tokens are loaded
  useEffect(() => {
    if (registeredTokens && registeredTokens.length > 0 && !isLoadingTokens) {
      // Check if current sourceToken is in the registered tokens
      const currentTokenRegistered = registeredTokens.some((token) => isAddressEqual(token.tokenAddress, sourceToken));

      // If not, set to first available registered token
      if (!currentTokenRegistered) {
        // Try to find preferred tokens in order
        const tokens = getTokens();
        const preferredTokens = [
          tokens.USDT?.l1Address,
          tokens.DAI?.l1Address,
          tokens.WETH?.l1Address,
          tokens.USDC?.l1Address,
        ].filter(Boolean) as Address[];

        const availablePreferredToken = preferredTokens.find((addr) =>
          registeredTokens.some((token) => isAddressEqual(token.tokenAddress, addr)),
        );

        if (availablePreferredToken) {
          setSourceToken(availablePreferredToken);
        } else if (registeredTokens[0]) {
          // Fall back to first registered token
          setSourceToken(registeredTokens[0].tokenAddress);
        }
      }
    }
  }, [registeredTokens, isLoadingTokens, sourceToken]);

  const sourceTokenData = registeredTokens?.find((token) => isAddressEqual(token.tokenAddress, sourceToken));
  const tokenSymbol = sourceTokenData?.tokenSymbol;
  const tokenDecimals = sourceTokenData?.tokenDecimals;

  const isReadyToReview = l1AccountAddress && l2AccountAddress;

  const labels = BRIDGE_LABELS[bridgeType];
  const TransferFromIcon = labels.transferFromIcon;
  const TransferToIcon = labels.transferToIcon;

  return (
    <Card
      className="max-w-lg mx-auto my-16"
      classNames={{
        base: "turnstile-card-border",
        header: "px-6 py-4",
        body: "px-6 py-2",
        footer: "p-6 pt-4",
      }}
    >
      <CardHeader className="flex flex-col items-start">
        <p className="text-lg font-semibold">Bridge Asset</p>
        <p className="text-sm text-[#AFBDC7]">Transfer your tokens from one network to Aztec Network.</p>
      </CardHeader>

      <CardBody className="items-center">
        <div className="w-full flex flex-col gap-2">
          <div className="flex justify-between">
            <span className="font-semibold">From</span>

            {tokenSymbol
              ? bridgeType === "deposit"
                ? l1AccountAddress && (
                    <L1TokenBalance
                      chainId={L1_CHAIN_ID}
                      tokenAddress={sourceToken}
                      l1AccountAddress={l1AccountAddress}
                      tokenSymbol={tokenSymbol}
                      decimals={tokenDecimals}
                    />
                  )
                : sourceTokenData?.l2Address && (
                    <L2TokenBalance l2TokenAddress={sourceTokenData.l2Address} tokenSymbol={tokenSymbol} decimals={tokenDecimals} />
                  )
              : null}
          </div>

          <div className="rounded-medium overflow-clip divide-y-2 divide-[#203E54] bg-[#33546C]">
            <div className="py-5 px-4 inline-flex gap-2 items-center">
              <TransferFromIcon className="size-6" />
              <span className="font-semibold">{labels.transferFrom}</span>
            </div>

            <div className="flex divide-x-2 divide-[#203E54]">
              <BridgeTokenSelector
                tokens={registeredTokens}
                selectedToken={sourceToken}
                onTokenChange={setSourceToken}
              />
              <Input
                type="number"
                label="Amount"
                labelPlacement="inside"
                aria-label="Amount to bridge"
                placeholder="Amount to bridge"
                value={transferAmount}
                onValueChange={setTransferAmount}
                radius="none"
                size="lg"
                className="flex-1"
                classNames={{
                  inputWrapper: "data-[hover=true]:bg-transparent shadow-none",
                  input: "placeholder:text-[#ADC0DD]",
                }}
              />
            </div>
          </div>
        </div>

        <Button
          isIconOnly
          color="primary"
          size="lg"
          aria-label="Swap"
          onPress={toggleBridgeType}
          className="bridge-swap-btn mt-6"
        >
          <ArrowUpDownIcon />
        </Button>

        <DividerDark />

        <div className="w-full flex flex-col gap-2">
          <div className="flex justify-start">
            <span className="font-semibold">To</span>
          </div>

          <div className="rounded-medium overflow-clip divide-y-2 divide-[#203E54] bg-default-100">
            <div className="py-5 px-4 inline-flex gap-2 items-center">
              <TransferToIcon className="size-6" />
              <span className="font-semibold">{labels.transferTo}</span>
            </div>

            <div className="flex items-center divide-x-2 divide-[#203E54]">
              {sourceTokenData ? (
                <div className="basis-36 flex gap-2 items-center px-4">
                  <Avatar name={sourceTokenData.tokenSymbol[0]} color="primary" classNames={{ base: "!size-6" }} />
                  <span className="font-semibold">{sourceTokenData.tokenSymbol}</span>
                </div>
              ) : (
                <div className="basis-36 flex justify-center items-center ">
                  <Spinner />
                </div>
              )}
              <Input
                type="number"
                label="Amount"
                labelPlacement="inside"
                aria-label="Bridged amount"
                placeholder="Bridged amount"
                value={transferAmount}
                radius="none"
                readOnly
                size="lg"
                className="flex-1"
                classNames={{
                  inputWrapper: "data-[hover=true]:bg-transparent shadow-none",
                  input: "placeholder:text-[#ADC0DD]",
                }}
              />
            </div>
          </div>
        </div>
      </CardBody>

      <CardFooter className="flex flex-col gap-2">
        <Button
          fullWidth
          size="lg"
          color={isReadyToReview ? "primary" : "default"}
          isDisabled={!isReadyToReview}
          radius="full"
          className="font-semibold h-16"
          onPress={() => {
            if (!tokenDecimals) {
              throw new Error("Token decimals not available");
            }

            if (!sourceTokenData) {
              throw new Error("Source token not registered");
            }

            if (!l1AccountAddress) {
              throw new Error("L1 account not found");
            }

            if (!l2AccountAddress) {
              throw new Error("L2 account not found");
            }

            onReview({
              bridgeType,
              l1AccountAddress,
              l2AccountAddress,
              sourceToken,
              tokenDecimals,
              tokenSymbol: sourceTokenData.tokenSymbol,
              transferAmount,
            });
          }}
        >
          Review
        </Button>
      </CardFooter>
    </Card>
  );
}

function useToggleBridgeType() {
  const [bridgeType, setBridgeType] = useState<BridgeType>("deposit");
  const toggleBridgeType = useCallback(() => {
    setBridgeType((prev) => (prev === "deposit" ? "withdraw" : "deposit"));
  }, []);
  return { bridgeType, toggleBridgeType };
}

function L1TokenBalance({
  chainId,
  tokenAddress,
  l1AccountAddress,
  tokenSymbol,
  decimals,
}: {
  chainId: typeof L1_CHAIN_ID;
  tokenAddress: Address;
  l1AccountAddress: Address;
  tokenSymbol: string;
  decimals?: number;
}) {
  const balanceQuery = useReadContract({
    chainId,
    address: tokenAddress,
    abi: erc20Abi,
    functionName: "balanceOf",
    args: [l1AccountAddress],
  });

  const decimalsQuery = useReadContract({
    chainId,
    address: tokenAddress,
    abi: erc20Abi,
    functionName: "decimals",
  });

  const actualDecimals = decimals ?? decimalsQuery.data;
  const isLoading = balanceQuery.isPending || (!decimals && decimalsQuery.isPending);

  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-[#AFBDC7]">Balance:</span>
      <Skeleton isLoaded={!isLoading} className="font-semibold rounded-xl">
        {balanceQuery.data != null && actualDecimals != null ? formatUnits(balanceQuery.data, actualDecimals) : "-"} {tokenSymbol}
      </Skeleton>
    </div>
  );
}

function L2TokenBalance({
  l2TokenAddress,
  tokenSymbol,
  decimals,
}: {
  l2TokenAddress: string;
  tokenSymbol: string;
  decimals?: number;
}) {
  const { publicBalance, isLoading } = useTokenBalance(l2TokenAddress);

  return (
    <div className="inline-flex items-center gap-1">
      <span className="text-[#AFBDC7]">Balance:</span>
      <Skeleton isLoaded={!isLoading} className="font-semibold rounded-xl">
        {publicBalance != null && decimals != null ? formatUnits(publicBalance, decimals) : "-"} {tokenSymbol}
      </Skeleton>
    </div>
  );
}
